import React, { useState } from "react";
import { useParams, useHistory, Link as RouterLink } from "react-router-dom";

import {
  Button,
  CssBaseline,
  TextField,
  Typography,
  CircularProgress,
  Link,
  Box,
  InputAdornment,
  IconButton,
} from "@material-ui/core";
import { Visibility, VisibilityOff } from "@material-ui/icons";
import { makeStyles } from "@material-ui/core/styles";
import { toast } from "react-toastify";

import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles((theme) => ({
  root: {
    minHeight: "100vh",
    display: "flex",
    [theme.breakpoints.down("md")]: {
      flexDirection: "column",
    },
  },
  leftPanel: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    padding: theme.spacing(4),
    backgroundColor: "#ffffff",
    position: "relative",
    minHeight: "100vh",
    [theme.breakpoints.up("md")]: {
      flex: "0 0 50%",
      maxWidth: "50%",
      minHeight: "auto",
    },
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(2),
    },
  },
  rightPanel: {
    flex: 1,
    backgroundImage:
      "linear-gradient(135deg, rgba(25, 118, 210, 0.85) 0%, rgba(21, 101, 192, 0.9) 100%), url('https://images.unsplash.com/photo-1562774053-701939374585?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80')",
    backgroundSize: "cover",
    backgroundPosition: "center",
    backgroundRepeat: "no-repeat",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    color: "#ffffff",
    position: "relative",
    [theme.breakpoints.down("md")]: {
      display: "none",
    },
  },
  formContainer: {
    width: "100%",
    maxWidth: "400px",
    padding: theme.spacing(3),
    animation: "$fadeInUp 0.8s ease-out",
  },
  "@keyframes fadeInUp": {
    "0%": { opacity: 0, transform: "translateY(30px)" },
    "100%": { opacity: 1, transform: "translateY(0)" },
  },
  logoContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    marginBottom: theme.spacing(4),
  },
  logo: {
    width: "80px",
    height: "80px",
    backgroundColor: "#1976d2",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "white",
    fontSize: "28px",
    fontWeight: "bold",
    marginBottom: theme.spacing(2),
    boxShadow: "0 4px 20px rgba(25, 118, 210, 0.3)",
  },
  title: {
    color: "#1976d2",
    fontWeight: "700",
    fontSize: "28px",
    marginBottom: theme.spacing(1),
    textAlign: "center",
  },
  subtitle: {
    color: "#666666",
    fontSize: "16px",
    textAlign: "center",
    marginBottom: theme.spacing(1),
  },
  form: {
    width: "100%",
  },
  textField: {
    marginBottom: theme.spacing(2),
    "& .MuiOutlinedInput-root": {
      borderRadius: "12px",
      backgroundColor: "#f8f9fa",
      "&:hover": { backgroundColor: "#f1f3f4" },
      "&.Mui-focused": {
        backgroundColor: "#ffffff",
        boxShadow: "0 0 0 2px rgba(25, 118, 210, 0.2)",
      },
    },
    "& .MuiInputLabel-root": {
      color: "#666666",
      "&.Mui-focused": { color: "#1976d2" },
    },
  },
  submitButton: {
    marginTop: theme.spacing(3),
    backgroundColor: "#1976d2",
    color: "#ffffff",
    fontWeight: "600",
    fontSize: "16px",
    padding: "14px",
    borderRadius: "12px",
    textTransform: "none",
    boxShadow: "0 4px 20px rgba(25, 118, 210, 0.3)",
    "&:hover": {
      backgroundColor: "#1565c0",
      boxShadow: "0 6px 25px rgba(25, 118, 210, 0.4)",
      transform: "translateY(-2px)",
    },
    transition: "all 0.3s ease",
  },
  rightPanelContent: {
    textAlign: "center",
    padding: theme.spacing(4),
    maxWidth: "500px",
  },
  rightPanelTitle: {
    fontSize: "36px",
    fontWeight: "700",
    marginBottom: theme.spacing(2),
    textShadow: "0 2px 4px rgba(0,0,0,0.3)",
  },
  rightPanelSubtitle: {
    fontSize: "18px",
    marginBottom: theme.spacing(3),
    opacity: 0.9,
    lineHeight: 1.6,
  },
  footer: {
    position: "absolute",
    bottom: theme.spacing(2),
    left: "50%",
    transform: "translateX(-50%)",
    textAlign: "center",
    color: "#999999",
    fontSize: "12px",
  },
}));

const ResetPassword = () => {
  const classes = useStyles();
  const { token } = useParams();
  const history = useHistory();

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (password.length < 6) {
      toast.error("La contraseña debe tener al menos 6 caracteres.");
      return;
    }

    if (password !== confirmPassword) {
      toast.error("Las contraseñas no coinciden.");
      return;
    }

    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      toast.success("Contraseña actualizada exitosamente.");
      history.push("/login");
    } catch (err) {
      toastError(err);
    }
    setLoading(false);
  };

  return (
    <div className={classes.root}>
      <CssBaseline />

      {/* Panel Izquierdo - Formulario */}
      <div className={classes.leftPanel}>
        <div className={classes.formContainer}>
          <div className={classes.logoContainer}>
            <div className={classes.logo}>UTS</div>
            <Typography component="h1" className={classes.title}>
              Restablecer Contraseña
            </Typography>
            <Typography className={classes.subtitle}>
              Ingresa tu nueva contraseña para recuperar el acceso a tu cuenta.
            </Typography>
          </div>

          <form className={classes.form} onSubmit={handleSubmit}>
            <TextField
              variant="outlined"
              required
              fullWidth
              label="Nueva Contraseña"
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoFocus
              className={classes.textField}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword((v) => !v)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              variant="outlined"
              required
              fullWidth
              label="Confirmar Contraseña"
              type={showPassword ? "text" : "password"}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className={classes.textField}
              error={
                confirmPassword.length > 0 && password !== confirmPassword
              }
              helperText={
                confirmPassword.length > 0 && password !== confirmPassword
                  ? "Las contraseñas no coinciden"
                  : ""
              }
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              className={classes.submitButton}
              disabled={loading || !password || !confirmPassword}
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                "Restablecer Contraseña"
              )}
            </Button>
          </form>

          <Box textAlign="center" mt={2}>
            <Link
              component={RouterLink}
              to="/login"
              style={{
                color: "#1976d2",
                textDecoration: "none",
                fontSize: "14px",
              }}
            >
              Volver al inicio de sesion
            </Link>
          </Box>
        </div>

        <div className={classes.footer}>
          <Typography variant="body2">
            © 2025 Unidades Tecnologicas de Santander
          </Typography>
        </div>
      </div>

      {/* Panel Derecho */}
      <div className={classes.rightPanel}>
        <div className={classes.rightPanelContent}>
          <Typography className={classes.rightPanelTitle}>
            Crea tu nueva contraseña
          </Typography>
          <Typography className={classes.rightPanelSubtitle}>
            Elige una contraseña segura de al menos 6 caracteres para proteger
            tu cuenta.
          </Typography>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
